import { useCallback, useEffect, useRef, useState } from 'react';
import { playArcadeSound, unlockArcadeAudio } from '../../shared/audio/audio';
import {
  COARSE_POINTER_QUERY,
  canUseTouchControls,
  readPerformanceDebugEnabled,
  readStartLevelIndex,
} from '../../app/startOptions';
import { trackEvent } from '../../app/telemetry';
import GameController from '../../shared/controls/GameController';
import { movementFlagsFromInput } from '../../shared/controls/inputState';
import { useKeyboardControls } from '../../shared/controls/useKeyboardControls';
import {
  createPerformanceStats,
  getEntityCounts,
  recordPerformanceFrame,
} from '../../app/performanceTelemetry';
import {
  INITIAL_LIVES,
  createEnemy,
  createInitialGameState,
  createLevelStartState,
  getLevelConfig,
  getDueWaveEnemies,
  getDueHazards,
  updateGame,
} from './logic/gameUtils';
import { createAutoFireBursts } from './logic/autoFire';
import {
  advanceCheckpoint,
  createCheckpoint,
} from './logic/restartCheckpoint';
import {
  readMaxScore,
  readRecords,
  writeMaxScore,
  writeRecords,
} from '../../shared/storage/localRecords';
import Hud from './components/Hud';
import Playfield from './components/Playfield';
import { shooterDefinition } from './shooterDefinition';

const FRAME_MS = 1000 / 60;
const LEVEL_ADVANCE_SECONDS = 3;
const LEVEL_ADVANCE_DELAY_MS = LEVEL_ADVANCE_SECONDS * 1000;
const MOBILE_EXPLOSION_RENDER_LIMIT = 5;
const MOBILE_MUZZLE_FLASH_RENDER_LIMIT = 3;
const PERFORMANCE_WARNING_INTERVAL_MS = 10000;

function ShooterGame({
  definition = shooterDefinition,
  onReturnToLibrary,
  selectedPlayableIcon = 'shooter',
}) {
  const levels = definition.levels;
  const storageNamespace = definition.storageNamespace;
  const startLevelIndexRef = useRef(readStartLevelIndex(window.location.search, levels.length));
  const [game, setGame] = useState(() =>
    createInitialGameState({
      levelIndex: startLevelIndexRef.current,
      practiceMode: startLevelIndexRef.current > 0,
      levels,
    })
  );
  const [maxScore, setMaxScore] = useState(() => readMaxScore(window.localStorage, storageNamespace));
  const [, setRecords] = useState(() => readRecords(window.localStorage, storageNamespace));
  const [showTouchControls, setShowTouchControls] = useState(canUseTouchControls);
  const [levelAdvanceSeconds, setLevelAdvanceSeconds] = useState(null);
  const performanceDebugRef = useRef(readPerformanceDebugEnabled());
  const gameRef = useRef(game);
  const inputRef = useRef({
    left: false,
    right: false,
    up: false,
    down: false,
  });
  const lastFrameRef = useRef(null);
  const animationRef = useRef(null);
  const enemyIdRef = useRef(1);
  const hazardIdRef = useRef(1);
  const bulletIdRef = useRef(1);
  const shotCooldownRef = useRef(0);
  const enemySpawnRef = useRef(0);
  const previousStatusRef = useRef(game.status);
  const previousLivesRef = useRef(game.lives);
  const previousBossHealthRef = useRef(null);
  const bossIntroSoundIdsRef = useRef(new Set());
  const explosionSoundIdsRef = useRef(new Set());
  const recordedLevelStatusesRef = useRef(new Set());
  const checkpointRef = useRef(createCheckpoint(startLevelIndexRef.current, 0));
  const performanceRef = useRef(createPerformanceStats(performanceDebugRef.current));
  const pausePressedRef = useRef(false);
  const lastPerformanceWarningAtRef = useRef(0);

  useEffect(() => {
    gameRef.current = game;
  }, [game]);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;

    if (game.status !== previousStatus) {
      if (game.status === 'levelComplete') {
        playArcadeSound('levelClear');
        trackEvent('shooter.level_completed', {
          enemiesDefeated: game.enemiesDefeated,
          gameId: definition.id,
          levelIndex: game.levelIndex,
          score: game.score,
        });
      }

      if (game.status === 'won') {
        playArcadeSound('victory');
        trackEvent('shooter.won', {
          gameId: definition.id,
          levelIndex: game.levelIndex,
          score: game.score,
        });
      }

      if (game.status === 'gameOver') {
        trackEvent('shooter.game_over', {
          gameId: definition.id,
          levelIndex: game.levelIndex,
          score: game.score,
        });
      }

      previousStatusRef.current = game.status;
    }
  }, [definition.id, game.enemiesDefeated, game.levelIndex, game.score, game.status]);

  useEffect(() => {
    if (game.lives < previousLivesRef.current) {
      playArcadeSound('damage');
    }

    previousLivesRef.current = game.lives;
  }, [game.lives]);

  useEffect(() => {
    game.explosions.forEach((explosion) => {
      if (!explosionSoundIdsRef.current.has(explosion.id)) {
        explosionSoundIdsRef.current.add(explosion.id);
        playArcadeSound('explosion');
      }
    });
  }, [game.explosions]);

  useEffect(() => {
    if (
      game.boss &&
      game.bossWarningMs > 0 &&
      !bossIntroSoundIdsRef.current.has(game.boss.id)
    ) {
      bossIntroSoundIdsRef.current.add(game.boss.id);
      playArcadeSound('bossIntro');
    }

    const previousBossHealth = previousBossHealthRef.current;

    if (
      game.boss &&
      previousBossHealth !== null &&
      game.boss.health < previousBossHealth
    ) {
      playArcadeSound('bossHit');
    }

    previousBossHealthRef.current = game.boss?.health ?? null;
  }, [game.boss, game.bossWarningMs]);

  useEffect(() => {
    if (game.practiceMode || !['gameOver', 'won'].includes(game.status) || game.score <= maxScore) {
      return;
    }

    writeMaxScore(game.score, window.localStorage, storageNamespace);
    setMaxScore(game.score);
  }, [game.practiceMode, game.score, game.status, maxScore, storageNamespace]);

  useEffect(() => {
    if (!['levelComplete', 'won'].includes(game.status)) {
      return;
    }

    const mode = game.practiceMode ? 'practice' : 'normal';
    const recordKey = `${mode}:${game.levelIndex}:${game.status}`;

    if (recordedLevelStatusesRef.current.has(recordKey)) {
      return;
    }

    recordedLevelStatusesRef.current.add(recordKey);
    setRecords((currentRecords) => {
      const nextRecords = {
        normal: {
          ...currentRecords.normal,
          completedSectors: [...(currentRecords.normal.completedSectors ?? [])],
          badges: { ...(currentRecords.normal.badges ?? {}) },
        },
        practice: {
          ...currentRecords.practice,
          completedSectors: [...(currentRecords.practice.completedSectors ?? [])],
          bestBySector: { ...(currentRecords.practice.bestBySector ?? {}) },
          badges: { ...(currentRecords.practice.badges ?? {}) },
        },
      };
      const sectorKey = String(game.levelIndex + 1);
      const modeRecords = nextRecords[mode];

      if (!modeRecords.completedSectors.includes(game.levelIndex)) {
        modeRecords.completedSectors.push(game.levelIndex);
      }

      if (mode === 'normal') {
        modeRecords.bestScore = Math.max(modeRecords.bestScore ?? 0, game.score);
      } else {
        modeRecords.bestBySector[sectorKey] = Math.max(
          modeRecords.bestBySector[sectorKey] ?? 0,
          game.score
        );
      }

      if (game.challengeSummary?.perfectClear) {
        modeRecords.badges[`sector-${sectorKey}-perfect`] = true;
      }

      if (!(game.sectorTookDamage ?? false)) {
        modeRecords.badges[`sector-${sectorKey}-no-damage`] = true;
      }

      writeRecords(nextRecords, window.localStorage, storageNamespace);
      return nextRecords;
    });
  }, [
    game.challengeSummary,
    game.levelIndex,
    game.practiceMode,
    game.score,
    game.sectorTookDamage,
    game.status,
    storageNamespace,
  ]);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(COARSE_POINTER_QUERY);

    if (!mediaQuery) {
      return undefined;
    }

    const updateTouchControls = () => {
      setShowTouchControls(canUseTouchControls());
    };

    mediaQuery.addEventListener?.('change', updateTouchControls);
    mediaQuery.addListener?.(updateTouchControls);

    return () => {
      mediaQuery.removeEventListener?.('change', updateTouchControls);
      mediaQuery.removeListener?.(updateTouchControls);
    };
  }, []);

  const resetClock = useCallback(() => {
    lastFrameRef.current = null;
    shotCooldownRef.current = 0;
    enemySpawnRef.current = 0;
  }, []);

  const startLevel = useCallback((levelIndex, score = 0, carriedEffects = {}, options = {}) => {
    unlockArcadeAudio();
    enemyIdRef.current = 1;
    hazardIdRef.current = 1;
    bulletIdRef.current = 1;
    explosionSoundIdsRef.current = new Set();
    bossIntroSoundIdsRef.current = new Set();
    previousBossHealthRef.current = null;
    previousStatusRef.current = 'playing';
    previousLivesRef.current = INITIAL_LIVES;
    recordedLevelStatusesRef.current = new Set();
    inputRef.current = { left: false, right: false, up: false, down: false };
    resetClock();
    const level = getLevelConfig(levelIndex, levels);
    enemySpawnRef.current = level.waves?.length ? 0 : level.spawnRateMs;
    trackEvent('shooter.level_started', {
      gameId: definition.id,
      levelIndex,
      practiceMode: options.practiceMode ?? false,
      selectedPlayableIcon,
    });
    setGame(createLevelStartState(levelIndex, score, carriedEffects, {
      practiceMode: options.practiceMode ?? false,
      levels,
    }));
  }, [definition.id, levels, resetClock, selectedPlayableIcon]);

  const startGame = useCallback(() => {
    const currentGame = gameRef.current;
    trackEvent('shooter.started', {
      gameId: definition.id,
      levelIndex: currentGame.levelIndex,
      practiceMode: currentGame.practiceMode,
      selectedPlayableIcon,
      status: currentGame.status,
    });

    if (currentGame.status === 'gameOver' && currentGame.practiceMode) {
      startLevel(currentGame.levelIndex, 0, {}, { practiceMode: true });
      return;
    }

    if (currentGame.status === 'gameOver') {
      checkpointRef.current = createCheckpoint(0, 0);
      startLevel(0, 0, {}, { practiceMode: false });
      return;
    }

    checkpointRef.current = createCheckpoint(startLevelIndexRef.current, 0);
    startLevel(startLevelIndexRef.current, 0, {}, {
      practiceMode: startLevelIndexRef.current > 0,
    });
  }, [definition.id, selectedPlayableIcon, startLevel]);

  const startPracticeLevel = useCallback((levelIndex) => {
    startLevel(levelIndex, 0, {}, { practiceMode: true });
  }, [startLevel]);

  const startNextLevel = useCallback(() => {
    const currentGame = gameRef.current;
    const nextLevelIndex = Math.min(gameRef.current.levelIndex + 1, levels.length - 1);
    checkpointRef.current = advanceCheckpoint(
      checkpointRef.current,
      nextLevelIndex,
      currentGame.score
    );
    startLevel(nextLevelIndex, currentGame.score, {
      rapidFireMs: currentGame.rapidFireMs,
      spreadShotMs: currentGame.spreadShotMs,
      optionDroneMs: currentGame.optionDroneMs,
      runStats: currentGame.runStats,
    }, {
      practiceMode: currentGame.practiceMode,
    });
  }, [levels.length, startLevel]);

  useEffect(() => {
    if (game.status !== 'levelComplete') {
      setLevelAdvanceSeconds(null);
      return undefined;
    }

    if (game.practiceMode) {
      setLevelAdvanceSeconds(0);
      return undefined;
    }

    setLevelAdvanceSeconds(LEVEL_ADVANCE_SECONDS);

    const countdownInterval = window.setInterval(() => {
      setLevelAdvanceSeconds((seconds) => Math.max(1, (seconds ?? LEVEL_ADVANCE_SECONDS) - 1));
    }, 1000);
    const advanceTimeout = window.setTimeout(() => {
      startNextLevel();
    }, LEVEL_ADVANCE_DELAY_MS);

    return () => {
      window.clearInterval(countdownInterval);
      window.clearTimeout(advanceTimeout);
    };
  }, [game.practiceMode, game.status, startNextLevel]);

  const applyControllerInput = useCallback((input) => {
    if (gameRef.current.status === 'playing' && (input.moveX || input.moveY || input.primary || input.boost)) {
      unlockArcadeAudio();
    }

    // Handle pause toggle
    if (input.pause && !pausePressedRef.current && gameRef.current.status === 'playing') {
      pausePressedRef.current = true;
      setGame((current) => ({
        ...current,
        status: 'paused',
      }));
      return;
    } else if (input.pause && !pausePressedRef.current && gameRef.current.status === 'paused') {
      pausePressedRef.current = true;
      setGame((current) => ({
        ...current,
        status: 'playing',
      }));
      return;
    }

    if (!input.pause) {
      pausePressedRef.current = false;
    }

    inputRef.current = movementFlagsFromInput(input);
  }, []);

  const confirmControllerInput = useCallback(() => {
    if (gameRef.current.status === 'playing') {
      return;
    }

    if (gameRef.current.status === 'levelComplete') {
      startNextLevel();
    } else {
      startGame();
    }
  }, [startGame, startNextLevel]);

  useKeyboardControls({
    onConfirm: confirmControllerInput,
    onInputChange: applyControllerInput,
  });

  useEffect(() => {
    const tick = (now) => {
      const previous = lastFrameRef.current ?? now;
      const rawDeltaMs = now - previous;
      const deltaMs = Math.min(rawDeltaMs, FRAME_MS * 3);
      lastFrameRef.current = now;

      if (gameRef.current.status === 'playing') {
        if (
          rawDeltaMs > FRAME_MS * 4 &&
          now - lastPerformanceWarningAtRef.current > PERFORMANCE_WARNING_INTERVAL_MS
        ) {
          lastPerformanceWarningAtRef.current = now;
          trackEvent('shooter.performance_warning', {
            frameMs: Math.round(rawDeltaMs),
            gameId: definition.id,
            levelIndex: gameRef.current.levelIndex,
            status: gameRef.current.status,
          });
        }

        const currentLevel = getLevelConfig(gameRef.current.levelIndex, levels);
        shotCooldownRef.current = Math.max(0, shotCooldownRef.current - deltaMs);
        enemySpawnRef.current -= deltaMs;

        let spawnedEnemies = [];
        if (currentLevel.waves?.length) {
          spawnedEnemies = getDueWaveEnemies({
            level: currentLevel,
            elapsedMs: gameRef.current.levelElapsedMs + deltaMs,
            enemiesSpawned: gameRef.current.enemiesSpawned,
            nextEnemyId: enemyIdRef.current,
          });
          enemyIdRef.current += spawnedEnemies.length;
        } else if (
          enemySpawnRef.current <= 0 &&
          gameRef.current.enemiesSpawned < currentLevel.enemyCount
        ) {
          spawnedEnemies.push(createEnemy(enemyIdRef.current, currentLevel));
          enemyIdRef.current += 1;
          enemySpawnRef.current = currentLevel.spawnRateMs;
        }

        const spawnedHazards = getDueHazards({
          level: currentLevel,
          elapsedMs: gameRef.current.levelElapsedMs + deltaMs,
          hazardsSpawned: gameRef.current.hazardsSpawned,
          nextHazardId: hazardIdRef.current,
        });
        hazardIdRef.current += spawnedHazards.length;

        let newBullets = [];

        if (shotCooldownRef.current <= 0) {
          const autoFire = createAutoFireBursts(gameRef.current, bulletIdRef.current);
          newBullets = autoFire.bullets;
          bulletIdRef.current = autoFire.nextBulletId;
          shotCooldownRef.current = autoFire.cooldownMs;
          playArcadeSound('laser');
        }

        setGame((current) => {
          const updateStart = performanceDebugRef.current ? performance.now() : 0;
          const nextGame = updateGame(current, {
            deltaMs,
            input: inputRef.current,
            spawnedEnemies,
            spawnedHazards,
            newBullets,
            levels,
          });

          recordPerformanceFrame(
            performanceRef,
            {
              frameMs: deltaMs,
              updateMs: performanceDebugRef.current ? performance.now() - updateStart : 0,
              levelIndex: nextGame.levelIndex,
              status: nextGame.status,
              entityCounts: getEntityCounts(nextGame),
            },
            FRAME_MS
          );

          return nextGame;
        });
      }

      animationRef.current = window.requestAnimationFrame(tick);
    };

    animationRef.current = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(animationRef.current);
    };
  }, [definition.id, levels]);

  const gameOverRestart = game.status === 'gameOver'
    ? game.practiceMode
      ? { levelIndex: game.levelIndex, score: 0 }
      : { levelIndex: 0, score: 0 }
    : null;
  const restartLevelName = gameOverRestart
    ? getLevelConfig(gameOverRestart.levelIndex, levels).name
    : null;
  const mobilePerformanceMode = showTouchControls;

  return (
    <main
      className={`game-app ${definition.themeClass} level-${game.levelIndex + 1}${mobilePerformanceMode ? ' mobile-performance' : ''}`}
    >
      <section className="game-shell" aria-labelledby="app-title">
        <Hud
          levelName={game.levelName}
          gameTitle={definition.name}
          instructions={definition.instructions}
          levelIndex={game.levelIndex}
          levelCount={levels.length}
          score={game.score}
          enemiesDefeated={game.enemiesDefeated}
          enemyCount={game.levelEnemyCount}
          levelGoal={game.levelGoal}
          levelDurationMs={game.levelDurationMs}
          levelElapsedMs={game.levelElapsedMs}
          lives={game.lives}
          bossHealth={game.boss?.health}
          bossMaxHealth={game.boss?.maxHealth}
          rapidFireMs={game.rapidFireMs}
          spreadShotMs={game.spreadShotMs}
          optionDroneMs={game.optionDroneMs}
        />

        <Playfield
          game={game}
          gameTitle={definition.name}
          playfieldLabel={definition.playfieldLabel}
          levelCount={levels.length}
          levelAdvanceSeconds={levelAdvanceSeconds}
          onStartGame={startGame}
          onStartNextLevel={startNextLevel}
          onStartPracticeLevel={startPracticeLevel}
          practiceMode={game.practiceMode}
          restartLevelName={restartLevelName}
          onReturnToLibrary={onReturnToLibrary}
          maxExplosions={mobilePerformanceMode ? MOBILE_EXPLOSION_RENDER_LIMIT : undefined}
          maxMuzzleFlashes={mobilePerformanceMode ? MOBILE_MUZZLE_FLASH_RENDER_LIMIT : undefined}
          selectedPlayableIcon={selectedPlayableIcon}
        />

        {showTouchControls && game.status === 'playing' && (
          <GameController
            actions={[
              { control: 'primary', label: 'Shoot', text: 'A' },
              { control: 'pause', label: 'Pause', text: '⏸' },
            ]}
            ariaLabel="Shooter touch controller"
            className="game-controller-shooter"
            movementLabel="Move ship"
            onInputChange={applyControllerInput}
          />
        )}

        {showTouchControls && game.status === 'paused' && (
          <GameController
            actions={[
              { control: 'pause', label: 'Resume', text: '▶' },
            ]}
            ariaLabel="Shooter pause controller"
            className="game-controller-shooter"
            movementLabel="Move ship"
            onInputChange={applyControllerInput}
          />
        )}
      </section>
    </main>
  );
}

export default ShooterGame;
